/**
 * Wikidata lookup using direct Entity API for fast lookups.
 * This bypasses the slower SPARQL search endpoint for known QIDs.
 */

import type { WikidataComposer, WikidataWork } from '@brent/shared/types'

// Re-export types for convenience
export type { WikidataComposer, WikidataWork }

/**
 * Get composer details by Wikidata ID
 * Fetches directly from Wikidata Entity API for fast lookups
 *
 * @param composerId - Wikidata QID (e.g., "Q7304" for Gustav Mahler)
 * @returns Composer details
 */
export async function getComposerById(composerId: string): Promise<WikidataComposer> {
  // Fetch directly from Wikidata Entity API for faster lookups
  const entityUrl = `https://www.wikidata.org/wiki/Special:EntityData/${composerId}.json`

  const response = await fetch(entityUrl)
  if (!response.ok) {
    throw new Error(`Failed to fetch composer ${composerId}: ${response.statusText}`)
  }

  const data = await response.json()
  const entity = data.entities[composerId]

  if (!entity) {
    throw new Error(`Composer not found: ${composerId}`)
  }

  // Extract label (name)
  const label = entity.labels?.en?.value
  if (!label) {
    throw new Error(`No English label found for composer: ${composerId}`)
  }

  // Extract birth year (P569)
  let birthYear: number | undefined
  const birthClaims = entity.claims?.P569
  if (birthClaims && birthClaims.length > 0) {
    const birthValue = birthClaims[0].mainsnak?.datavalue?.value?.time
    if (birthValue) {
      // Parse "+1860-07-07T00:00:00Z" to extract year
      const match = birthValue.match(/^[+-]?(\d+)-/)
      if (match) {
        birthYear = parseInt(match[1], 10)
      }
    }
  }

  // Extract death year (P570)
  let deathYear: number | undefined
  const deathClaims = entity.claims?.P570
  if (deathClaims && deathClaims.length > 0) {
    const deathValue = deathClaims[0].mainsnak?.datavalue?.value?.time
    if (deathValue) {
      const match = deathValue.match(/^[+-]?(\d+)-/)
      if (match) {
        deathYear = parseInt(match[1], 10)
      }
    }
  }

  return {
    wikidataId: composerId,
    name: label,
    birthYear,
    deathYear,
  }
}

/**
 * Get work details by Wikidata ID
 * Fetches directly from Wikidata Entity API for fast lookups
 *
 * @param workId - Wikidata QID (e.g., "Q233")
 * @returns Work details
 */
export async function getWorkById(workId: string): Promise<WikidataWork> {
  // Fetch directly from Wikidata Entity API for faster lookups
  const entityUrl = `https://www.wikidata.org/wiki/Special:EntityData/${workId}.json`

  const response = await fetch(entityUrl)
  if (!response.ok) {
    throw new Error(`Failed to fetch work ${workId}: ${response.statusText}`)
  }

  const data = await response.json()
  const entity = data.entities[workId]

  if (!entity) {
    throw new Error(`Work not found: ${workId}`)
  }

  // Extract label (title)
  const label = entity.labels?.en?.value
  if (!label) {
    throw new Error(`No English label found for work: ${workId}`)
  }

  // Extract catalog number (P528)
  let catalogNumber: string | undefined
  const catalogClaims = entity.claims?.P528
  if (catalogClaims && catalogClaims.length > 0) {
    catalogNumber = catalogClaims[0].mainsnak?.datavalue?.value
  }

  // Extract year (P571 - inception)
  let year: number | undefined
  const yearClaims = entity.claims?.P571
  if (yearClaims && yearClaims.length > 0) {
    const yearValue = yearClaims[0].mainsnak?.datavalue?.value?.time
    if (yearValue) {
      const match = yearValue.match(/^[+-]?(\d+)-/)
      if (match) {
        year = parseInt(match[1], 10)
      }
    }
  }

  return {
    wikidataId: workId,
    title: label,
    catalogNumber,
    year,
  }
}
